
import Vue from 'vue';
import Component from "vue-class-component";
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import {
    UpsertCertificateTypeRequest,
    CertificateDocumentTypeApiModel,
    DocumentAttributesClient,
    factory,
    PositionOption,
    PositionsClient,
    PrintTemplatePostModel,
    ResultError,
    StcwIsClient,
    StcwIsListModel,
    StcwRuleLightModel,
    StcwRulesClient,
    SysAdminCertificateTypesClient,
    SysAdminCertificateTypeTemplatesClient,
    TempFileModel, IUpsertCertificateTypeRequest,
    PrintBackgroundOption,
    SysAdminPrintBackgroundTemplatesClient
} from "@/api-client";
import toasts from "@/components/Toasts";
import AdministrationDetailviewLayout from "@/features/administration/AdministrationDetailviewLayout.vue";
import FileUploader from "@/components/FileUploader.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import TrueFalseOptions from "@/features/shared/TrueFalseOptions";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import {Route} from "vue-router";
import {Prop, Watch} from "vue-property-decorator";
import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";
import ErrorsSummary from "@/components/ErrorsSummary.vue";
import VueJsonEditor from 'vue-json-editor';

@Component({
    components: {
        SectionPageHeading,
        HeadingWave,
        AdministrationDetailviewLayout,
        FileUploader,
        HeadingGroup,
        LabelOptional,
        ButtonWithConfirm,
        ErrorsSummary,
        VueJsonEditor
    }
})
export default class UpsertCertificateType extends Vue {
    async beforeRouteEnter(to: Route, from: Route, next: Function) {
        try {
            const printBackgroundTemplateOtions = await new SysAdminPrintBackgroundTemplatesClient().options();
            to.meta!.props.printBackgroundTemplateOtions = printBackgroundTemplateOtions;
            const positionOptions = await new PositionsClient().positionOptions();
            to.meta!.props.preloadedStcwRuleOptions = await new StcwRulesClient().list();
            to.meta!.props.preloadedPositionOptions = positionOptions;
            to.meta!.props.preloadedStcwIsOptions = await new StcwIsClient().list();
            to.meta!.props.preloadedCertificateDocumentTypeOptions = await new DocumentAttributesClient().certificateDocumentTypes();

            // Load certificate type templates
            const templateClient = new SysAdminCertificateTypeTemplatesClient();
            const templates = await templateClient.list();
            to.meta!.props.preloadedTemplateOptions = templates
                .filter(t => t.isActive)
                .map(t => ({ value: t.id, text: t.name }));

            if (to.params.id) {
                const existing = await new SysAdminCertificateTypesClient().get(to.params.id);

                to.meta!.props.preloadedModel = new UpsertCertificateTypeRequest({
                    id: existing.id,
                    nameEng: existing.name.english,
                    nameEst: existing.name.estonian,
                    legalNameEst: existing.legalName.estonian,
                    legalNameEng: existing.legalName.english,
                    prefix: existing.prefix,
                    certificateDocumentTypeCode: existing.certificateDocumentType.code,
                    stcwRuleId: existing.stcwRule?.id,
                    stcwIs: existing.stcwIs?.value,
                    emsaRestriction: existing.emsaRestriction,
                    additionalInfo: existing.additionalInfo,
                    actOfLaw: existing.actOfLaw,
                    hasPrintTemplate: !!existing.printTemplateName || !!existing.certificateTypeTemplateId || !!existing.printBackgroundTemplateId,
                    certificateTypeTemplateId: existing.certificateTypeTemplateId,
                    printBackgroundTemplateId: existing.printBackgroundTemplateId && printBackgroundTemplateOtions.map(item=>item.value).includes(existing.printBackgroundTemplateId) ? existing.printBackgroundTemplateId : null,
                    // printTemplate: undefined, // Files need to be re-added.
                    templateTextLiterals: existing.templateTextLiterals,
                    allowEndorsement: existing.allowEndorsement,
                    allowedEndorsementPositionIds: existing.allowedEndorsementPositions?.map(x => x.id) ?? [],
                    onlyForForeignSeagoingCertificates: existing.isOnlyForForeignCertificates,
                    positionId: existing.position?.id
                } as IUpsertCertificateTypeRequest);
            }
            next();
        } catch (e) {
            next(e);
        }
    }

    @Prop({required: false, default: () => factory.create(UpsertCertificateTypeRequest)})
    preloadedModel!: UpsertCertificateTypeRequest;

    @Prop({required: true})
    preloadedStcwRuleOptions!: StcwRuleLightModel[];

    @Prop({required: true})
    preloadedPositionOptions!: PositionOption[];

    @Prop({required: true})
    preloadedStcwIsOptions!: StcwIsListModel[];

    @Prop({required: true})
    preloadedCertificateDocumentTypeOptions!: CertificateDocumentTypeApiModel[];

    @Prop({required: false, default: () => []})
    preloadedTemplateOptions!: Array<{ value: string; text: string }>;

    @Prop({required: false})
    printBackgroundTemplateOtions?: PrintBackgroundOption[];

    model = this.preloadedModel;
    validationError: ResultError = ResultError.createEmpty();

    stcwRuleOptions = this.preloadedStcwRuleOptions;
    positionOptions = this.preloadedPositionOptions;
    stcwIsOptions = this.preloadedStcwIsOptions;
    certificateDocumentTypeOptions = this.preloadedCertificateDocumentTypeOptions;
    templateOptions = this.preloadedTemplateOptions;
    yesNoOptions = TrueFalseOptions.options;
    templateTextLiterals: Record<string, string> | null = null;
    templateTextLiteralsLoading = false;

    printTemplate = factory.create(PrintTemplatePostModel);
    uploadedPrintTemplates: TempFileModel[] = []; // Separate variable for uploaded templates since file uploader expects an array as it's model.

    allowedEndorsementPositions =
        (this.preloadedModel.allowedEndorsementPositionIds ?? [])
            .map(x => this.positionOptions.filter(y => y.value === x)[0])
            .filter(x => !!x);

    get isEditingExistingCertificateType(): boolean {
        return !!this.preloadedModel.id;
    }

    async created() {
        // Load template text literals if editing and has template selected
        if (this.isEditingExistingCertificateType && this.model.certificateTypeTemplateId) {
            await this.loadTemplateTextLiterals(this.model.certificateTypeTemplateId);
            // Only override with saved text literals if they exist (not null/undefined/empty)
            if (this.preloadedModel.templateTextLiterals) {
                try {
                    const savedLiterals = JSON.parse(this.preloadedModel.templateTextLiterals);
                    // Only use saved literals if they actually have content
                    if (savedLiterals && Object.keys(savedLiterals).length > 0) {
                        this.templateTextLiterals = savedLiterals;
                    }
                    // Otherwise keep the template's default literals that were loaded
                } catch (e) {
                    console.warn('Failed to parse saved template text literals, using defaults from template', e);
                }
            }
            // If no saved literals exist, templateTextLiterals will have the defaults from loadTemplateTextLiterals
        }
    }

    @Watch('model.certificateTypeTemplateId')
    async onTemplateSelectionChange(newValue: string | null, oldValue: string | null) {
        // If user selects "None" from the dropdown, automatically switch to "No" for print template
        if (newValue === null && this.model.hasPrintTemplate && !this.model.printBackgroundTemplateId) {
            this.model.hasPrintTemplate = false;
            this.templateTextLiterals = null;
        } else if (newValue && newValue !== oldValue) {
            // Always load new template's text literals when switching templates
            await this.loadTemplateTextLiterals(newValue, true);
        }
    }

    @Watch('model.printBackgroundTemplateId')
    async onPrintBackgroundSelectionChange(newValue: string | null, oldValue: string | null) {
        if (newValue === null && this.model.hasPrintTemplate && !this.model.certificateTypeTemplateId) {
            this.model.hasPrintTemplate = false;
            this.templateTextLiterals = null;
        }
    }

    @Watch('model.hasPrintTemplate')
    onHasPrintTemplateChange(newValue: boolean) {
        if (newValue) {
            // When switching to "Yes", auto-select the first template if available and none is selected
            if (!this.model.certificateTypeTemplateId && this.templateOptions.length > 0) {
                this.model.certificateTypeTemplateId = this.templateOptions[0].value;
            }
        } else {
            // When switching to "No", clear the template selection
            this.model.certificateTypeTemplateId = null;
        }
    }

    async save() {
        this.validationError.clear();

        if (this.model.hasPrintTemplate) {
            // If template ID is selected, we don't need to upload a file
            if (this.model.certificateTypeTemplateId) {
                this.model.printTemplate = null;
            } else if (this.uploadedPrintTemplates.length > 0) {
                // Legacy file upload for backward compatibility
                this.model.printTemplate = this.printTemplate;
                this.model.printTemplate.file = this.uploadedPrintTemplates[0];
            }
        } else {
            this.model.printTemplate = null;
            this.model.certificateTypeTemplateId = null;
            this.model.printBackgroundTemplateId = null;
        }

        this.model.allowedEndorsementPositionIds = this.model.allowEndorsement
            ? this.allowedEndorsementPositions.map(x => x.value)
            : [];

        // Add text literals if template is selected
        if (this.model.certificateTypeTemplateId && this.templateTextLiterals) {
            this.model.templateTextLiterals = JSON.stringify(this.templateTextLiterals);
        }

        await new SysAdminCertificateTypesClient().upsert(this.model)
            .then(
                () => {
                    this.$router.push({name: 'certificate-types'});
                    toasts.success(this.$ls.Administration_CertificateTypes_Toast_AddSuccess);
                },
                (errorResponse: ResultError) => {
                    this.validationError = errorResponse;
                    toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                });
    }

    removeEndorsementPosition(position: PositionOption) {
        this.allowedEndorsementPositions.splice(this.allowedEndorsementPositions.indexOf(position), 1);
    }

    async loadTemplateTextLiterals(templateId: string, forceUpdate = false) {
        this.templateTextLiteralsLoading = true;
        try {
            const templateClient = new SysAdminCertificateTypeTemplatesClient();
            const template = await templateClient.get(templateId);
            if (template.textLiterals) {
                // When forceUpdate is true (switching templates), always use the new template's literals
                // When editing existing certificate type without saved literals, use template's defaults
                // Otherwise preserve existing literals
                if (forceUpdate || !this.templateTextLiterals) {
                    this.templateTextLiterals = JSON.parse(template.textLiterals);
                }
                // Note: The created() method will override with saved literals if they exist
            } else {
                // If template has no text literals, clear the current ones
                this.templateTextLiterals = null;
            }
        } catch (error) {
            console.error('Error loading template text literals:', error);
            this.templateTextLiterals = null;
        } finally {
            this.templateTextLiteralsLoading = false;
        }
    }

    onJsonChange(value: Record<string, string>) {
        this.templateTextLiterals = value;
    }

    get currentCulture(): string {
        return (this as any).$i18n?.locale || 'en';
    }
}
